import { useState, useMemo, useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Briefcase, FileText, Calendar, MapPin, Building2, User,
  ExternalLink, Camera, Paperclip, Star, CheckCircle2, CircleDashed,
  Loader2, Flag, Truck, FolderOpen, CalendarRange, Radio, ClipboardList, Search, BookOpen, Wrench,
} from "lucide-react";
import SiteFolderScreen from "@/features/sitefolder/SiteFolderScreen";
import { withReturn } from "@/lib/returnTo";
import MopsGuideList from "@/features/mops/MopsGuideList";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Card, Pill, Button, Spinner, EmptyState } from "@/components/ui";
import { DeliveryCard } from "@/features/materials/ProjectMaterialsTab";
import { type DeliveryRow, DELIVERY_SELECT } from "@/features/materials/deliveries";
import { format, parseISO } from "date-fns";
import { lazy, Suspense } from "react";
import { ProjectStanddownBanner } from "@/features/hse/standdownShared";   // mig 425
import ProjectDefectsTab, { useProjectDefectBadge, DefectCountBadge } from "@/features/defects/ProjectDefectsTab";   // 9 Oct 2026
const DesignPanel = lazy(() => import("@/features/projects/DesignPanel"));

// =============================================================================
// FieldProjectDetailScreen — /projects/:projectId for field + subcontractor.
// Scrubbed view: no financials, no commercials, no HSE internals. Just what a
// crew member needs to do their job and upload to HOP.
//
// Tabs: Overview · Documents · HOP (linked out — lands in Commit C).
// T-010 HOP Commit A.
// =============================================================================

// 27 Sep (James, from the phone): "add MOP screen nav in the project top as
// well as design tab and schedule (view only for that site) — we have a few
// good tools they would benefit from". Schedule = the crew's plan cards that
// used to sit at the bottom of Overview; Design = the office's DesignPanel,
// read-only; MOPs = this job's MOPs, opened in the reader.
// 27 Sep (later): "make the site folder the field's main project interaction"
// — Documents, HOP, the site diary and the bookings live INSIDE the folder
// now; the top strip keeps the office-side reference tabs.
// 9 Oct 2026 (James): "let the field view so they can see the defect on their
// sites when they are curious" — a read-only Defects tab, same count + colour
// on the button as the office project page (features/defects/ProjectDefectsTab).
type TabKey = "folder" | "overview" | "schedule" | "design" | "mops" | "defects";

const STATUS_TONE: Record<string, "approved" | "pending" | "warning" | "default"> = {
  live:                 "approved",
  mobilising:           "pending",
  scheduled:            "default",
  acceptance_pending:   "warning",
  on_hold:              "warning",
  closed:               "default",
};

export default function FieldProjectDetailScreen() {
  const { projectId } = useParams<{ projectId: string }>();
  const user = useRequireAuth();
  const navigate = useNavigate();
  // mig 393 — the job opens on its Site folder (James, 27 Sep: "I don't want it hidden or nested away")
  // "Your horizon" deep-links /projects/:id#plan | #program — those cards live
  // on the Schedule tab now, so open there.
  const initialHash = useLocation().hash;
  const [tab, setTab] = useState<TabKey>(initialHash === "#plan" || initialHash === "#program" ? "schedule" : "folder");

  const defectBadge = useProjectDefectBadge(projectId);   // 9 Oct 2026 — hook stays above the early returns

  const { data: project, isLoading } = useQuery({
    queryKey: ["field_project", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      // Explicitly select only the non-financial columns. Even though RLS
      // doesn't gate column-level access on projects, this keeps the payload
      // honest — no contract_value, margin, PO value, etc. ever hits the
      // field-role browser. PM + supervisor are joined via their direct FKs
      // on the projects table.
      const { data, error } = await supabase
        .from("projects")
        .select(`
          project_id, project_code, project_name, status, project_type,
          notes, region_code,
          scheduled_start_date, scheduled_end_date,
          actual_start_date, actual_end_date,
          site_id,
          clients(legal_name, primary_contact_name, primary_contact_email, primary_contact_phone),
          sites(site_code, site_name, address_line1, suburb, state, postcode)
        `)
        .eq("project_id", projectId!)
        .maybeSingle();
      if (error) throw error;
      return data as any;
    },
  });

  // Who to contact. This card silently showed "Not assigned" for every field
  // user since the T-508 era: it embedded PM and supervisor straight off
  // public.employees, which field cannot read, and PostgREST returns null for
  // an RLS-filtered embed rather than an error - so the two phone numbers a
  // tech on site most needs were exactly the ones the screen ate, and nothing
  // ever logged a failure. app.project_contacts (migration 327) returns name
  // and mobile only, to people on the project, without opening the employees
  // row (which carries pay) to anyone.
  const { data: contacts = [] } = useQuery({
    queryKey: ["field_project_contacts", projectId],
    enabled: !!projectId,
    queryFn: async () => {
      const { data, error } = await supabase
        .schema("app")
        // "as any" until database.types.ts is regenerated after migration 327 -
        // the generated union of RPC names cannot know about a function that
        // does not exist yet on the machine that generated it.
        .rpc("project_contacts" as any, { p_project_id: projectId! });
      if (error) throw error;
      return (data ?? []) as { contact_role: string; full_name: string; mobile: string | null }[];
    },
  });

  // Deep-link anchors from "Your horizon" — /projects/:id#plan | #program.
  // The cards load async, so scroll on a short delay and once more after the
  // queries have very likely settled.
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash || !project) return;
    const target = hash.slice(1);
    const timers = [500, 1400].map(ms => setTimeout(() => {
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, ms));
    return () => timers.forEach(clearTimeout);
  }, [hash, project]);

  if (!user) return null;
  if (isLoading) {
    return <div className="p-6 flex justify-center"><Spinner size={20} /></div>;
  }
  if (!project) {
    return (
      <div className="p-6 max-w-xl mx-auto">
        <Card>
          <EmptyState
            title="Project not found"
            description="This project doesn't exist or you're not on its team."
            icon={Briefcase}
          />
          <div className="p-4 text-center">
            <Link to="/projects"><Button variant="secondary">Back to My jobs</Button></Link>
          </div>
        </Card>
      </div>
    );
  }

  const tone = STATUS_TONE[project.status] ?? "default";

  return (
    <div className="p-4 md:p-6 max-w-[1180px] mx-auto">
      <button
        onClick={() => navigate("/projects")}
        className="flex items-center gap-1.5 text-[12px] text-text-muted hover:text-text mb-3"
        type="button"
      >
        <ArrowLeft size={14} /> My jobs
      </button>

      {/* Header */}
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] font-mono text-text-muted">{project.project_code}</div>
          <h1 className="text-[22px] md:text-2xl font-semibold tracking-tight text-text">
            {project.project_name}
          </h1>
          <div className="text-text-muted text-[13px] mt-1 flex items-center gap-3 flex-wrap">
            {project.clients?.legal_name && (
              <span className="inline-flex items-center gap-1"><Building2 size={12} /> {project.clients.legal_name}</span>
            )}
            {project.project_type && <span className="capitalize">· {project.project_type}</span>}
          </div>
        </div>
        <Pill label={project.status} tone={tone} />
      </div>

      {/* Mig 425 (8 Oct 2026) — the crew see it the moment they open the job:
          red when work is stopped here (stand-down, or a carrier embargo),
          amber when a sister site is. Above the tabs so it is on every one. */}
      <ProjectStanddownBanner projectId={project.project_id} className="mb-4" />

      {/* Tabs */}
      <div className="mb-5 flex items-center gap-1 p-1 bg-ink-raised border border-line rounded-md max-w-full overflow-x-auto" style={{ scrollbarWidth: "none" }}>
        {([
          { key: "folder",    label: "Site folder", icon: FolderOpen },
          { key: "overview",  label: "Overview",  icon: Briefcase },
          { key: "schedule",  label: "Schedule",  icon: CalendarRange },
          { key: "design",    label: "Design",    icon: Radio },
          { key: "mops",      label: "MOPs",      icon: BookOpen },
          { key: "defects",   label: "Defects",   icon: Wrench },
        ] as const).map(t => (
          <button
            key={t.key}
            type="button"
            data-tab={t.key}
            onClick={e => { setTab(t.key); e.currentTarget.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" }); }}
            className={`shrink-0 whitespace-nowrap px-3 py-1.5 rounded text-[12px] transition-colors inline-flex items-center gap-1.5 ${
              tab === t.key
                ? "bg-accent/15 text-accent border border-accent/30"
                : "text-text-muted hover:text-text"
            }`}
          >
            <t.icon size={12} />
            {t.label}
            {t.key === "defects" && <DefectCountBadge badge={defectBadge} />}
          </button>
        ))}
      </div>

      {tab === "folder" && (
        <SiteFolderScreen projectId={project.project_id} embedded />
      )}
      {tab === "overview" && (
        <OverviewTab project={project} contacts={contacts} />
      )}
      {tab === "schedule" && (
        <ScheduleTab project={project} />
      )}
      {tab === "design" && (
        <Suspense fallback={<div className="py-10 flex justify-center"><Spinner size={18} /></div>}>
          <div className="text-[12px] text-text-muted mb-3">The design as the office recorded it — read-only here. Ask the PM if something on site doesn't match.</div>
          <DesignPanel parent={{ kind: "project", id: project.project_id, code: project.project_code, name: project.project_name, siteId: project.site_id ?? null }} canEdit={false} embedded />
        </Suspense>
      )}
      {tab === "mops" && (
        <MopsTab projectId={project.project_id} />
      )}
      {tab === "defects" && (
        <ProjectDefectsTab projectId={project.project_id} canManage={false} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------

function OverviewTab({ project, contacts }: {
  project: any;
  contacts: { contact_role: string; full_name: string; mobile: string | null }[];
}) {
  const site = project.sites;
  const startDate = project.actual_start_date ?? project.scheduled_start_date;
  const endDate   = project.actual_end_date ?? project.scheduled_end_date;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Scope */}
      <Card className="md:col-span-2">
        <div className="p-5">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2">Scope</div>
          {project.notes ? (
            <p className="text-text text-[13px] whitespace-pre-wrap">{project.notes}</p>
          ) : (
            <div className="text-text-muted text-[13px] italic">
              No scope recorded yet — check with your supervisor if unsure what's in scope today.
            </div>
          )}
        </div>
      </Card>

      {/* Site */}
      <Card>
        <div className="p-5">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2">Site</div>
          {site ? (
            <>
              <div className="text-text font-medium">{site.site_name ?? site.site_code}</div>
              <div className="text-[13px] text-text-muted mt-1 space-y-0.5">
                {site.address_line1 && <div>{site.address_line1}</div>}
                <div>{[site.suburb, site.state, site.postcode].filter(Boolean).join(" · ")}</div>
              </div>
              {site.site_code && (
                <div className="text-[11px] font-mono text-text-dim mt-2">{site.site_code}</div>
              )}
            </>
          ) : (
            <div className="text-text-muted text-[13px]">Site not linked.</div>
          )}
        </div>
      </Card>

      {/* Dates */}
      <Card>
        <div className="p-5">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2">Dates</div>
          <div className="space-y-2 text-[13px]">
            <Row icon={Calendar} label="Start" value={startDate ? format(parseISO(startDate), "dd MMM yyyy") : "—"} />
            <Row icon={Calendar} label="End"   value={endDate ? format(parseISO(endDate), "dd MMM yyyy") : "—"} />
            {project.region_code && (
              <Row icon={MapPin} label="Region" value={project.region_code} />
            )}
          </div>
        </div>
      </Card>

      {/* Team contacts */}
      <Card className="md:col-span-2">
        <div className="p-5">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2">Who to contact</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ContactCard role="Project manager"
              person={contacts.find(c => c.contact_role === "Project manager") ?? null} />
            <ContactCard role="Site supervisor"
              person={contacts.find(c => c.contact_role === "Supervisor") ?? null} />
          </div>
        </div>
      </Card>

      {/* Client contact — only primary, no financial contact fields */}
      {project.clients?.primary_contact_name && (
        <Card className="md:col-span-2">
          <div className="p-5">
            <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2">Client contact</div>
            <div className="text-text font-medium">{project.clients.primary_contact_name}</div>
            <div className="text-[12px] text-text-muted mt-1 space-x-3">
              {project.clients.primary_contact_phone && <span>{project.clients.primary_contact_phone}</span>}
              {project.clients.primary_contact_email && <span>{project.clients.primary_contact_email}</span>}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

// Schedule tab (27 Sep) — the crew's view of the plan, view-only: today's
// work, the program gantt (Coretel's lines), the build plan's key dates and
// the deliveries. These cards lived at the foot of Overview; now they have
// their own tab so they're one tap from the folder.
function ScheduleTab({ project }: { project: any }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div id="plan"><TodaysWorkCard projectId={project.project_id} /></div>
      <div id="program"><FieldProgramCard projectId={project.project_id} defaultOpen /></div>
      <BuildPlanCard projectId={project.project_id} />
      <FieldDeliveriesCard projectId={project.project_id} />
    </div>
  );
}

// MOPs tab (27 Sep) — James: "when I said MOPs I was mainly referring to
// the /mops screen that has the client and carrier MOPs — all the different
// work instructions like how to install a Telstra cabinet or an NBN MW link
// or a LeBlanc monopole … they should be able to see the carrier MOPs as
// reference guides / library." So: this job's own MOP(s) first when the
// office has written one (not every job gets one), then the library —
// MopsGuideList, the same list the field /mops page shows.
function MopsTab({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const { data: jobMops = [] } = useQuery({
    queryKey: ["field_project_mops", projectId],
    queryFn: async () => {
      const { data } = await (supabase as any).from("mops")
        .select("mop_id, mop_number, title, status, version, approved_at")
        .eq("project_id", projectId).neq("status", "superseded")
        .order("created_at", { ascending: false });
      return (data ?? []) as any[];
    },
  });
  const returnTo = `/projects/${projectId}`;

  return (
    <div className="space-y-5">
      {jobMops.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-1.5 px-1">This job's MOP — Coretel</div>
          <Card>
            <div className="divide-y divide-line">
              {jobMops.map(m => {
                const approved = m.status === "approved";
                return (
                  <button key={m.mop_id} type="button" onClick={() => navigate(withReturn(`/hse/mops/${m.mop_id}`, returnTo))} className="w-full px-3 py-3 flex items-center gap-3 text-left active:bg-ink-raised">
                    <div className={`w-11 h-11 rounded-md border flex items-center justify-center flex-shrink-0 ${approved ? "bg-ok/10 border-ok/30 text-ok" : "bg-ink-raised border-line text-text-muted"}`}><ClipboardList size={18} /></div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] text-text leading-snug line-clamp-2">{m.title}</div>
                      <div className="text-[11px] text-text-muted mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-accent">{m.mop_number}</span>
                        {approved ? <Pill label={`approved v${m.version}`} tone="approved" small /> : <Pill label="DRAFT — NOT FOR USE" tone="warning" small />}
                      </div>
                    </div>
                    <ExternalLink size={14} className="text-text-muted flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          </Card>
        </div>
      )}
      <MopsGuideList title="Work instructions — carrier & supplier MOPs"/>
    </div>
  );
}

function Row({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <Icon size={12} className="text-text-muted flex-shrink-0" />
      <span className="text-text-muted w-16 flex-shrink-0">{label}</span>
      <span className="text-text">{value}</span>
    </div>
  );
}

function ContactCard({ role, person }: { role: string; person: any }) {
  if (!person) {
    return (
      <div className="p-3 rounded-md bg-ink-raised border border-line">
        <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">{role}</div>
        <div className="text-[13px] text-text-muted italic mt-1">Not assigned</div>
      </div>
    );
  }
  const name = person.full_name || "—";
  return (
    <div className="p-3 rounded-md bg-ink-raised border border-line">
      <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono">{role}</div>
      <div className="text-text font-medium mt-1 flex items-center gap-1.5">
        <User size={12} className="text-text-muted" /> {name}
      </div>
      {person.mobile && (
        <a href={`tel:${person.mobile}`} className="text-[12px] text-accent hover:underline block mt-1">
          {person.mobile}
        </a>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// FieldProgramCard — the whole program, VIEW-ONLY, for the crew (James, 3 Sep:
// "expand the full GANTT in view only and click on the line brings up notes
// and the sub activities, booking details if clicked on — they can have a
// booking contact for the plant"). Collapsed behind a button; tap a line for
// the detail sheet. No dragging, no editing — ticking subs is the one write.
// -----------------------------------------------------------------------------
const FP_LANE_C: Record<string, string> = {
  CIVILS: "#4f97d7", STRUCTURE: "#7a8aa0", ICIO: "#e2661a", POWER: "#37a97f", ACCEPT: "#9b7bd4",
};
function FieldProgramCard({ projectId, defaultOpen = false }: { projectId: string; defaultOpen?: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(defaultOpen);
  const [sel, setSel] = useState<any | null>(null);
  const { data } = useQuery({
    queryKey: ["field_program_full", projectId],
    enabled: open,
    queryFn: async () => {
      const [acts, subs, lanes, bks] = await Promise.all([
        (supabase as any).from("project_program_activities")
          .select("activity_id, code, wbs, lane, owner, name, resource, kind, start_date, end_date, days, notes, plant_needed, is_key, booking_id, sort_order")
          .eq("project_id", projectId).eq("is_active", true).order("sort_order"),
        (supabase as any).from("project_program_subactivities")
          .select("sub_id, activity_id, name, sort_order, done, done_at, crew")
          .eq("project_id", projectId).order("sort_order"),
        (supabase as any).from("project_program_lanes")
          .select("lane_code, color, sort_order").eq("project_id", projectId).order("sort_order"),
        (supabase as any).from("project_bookings")
          .select("booking_id, title, booking_type, status, scheduled_start, scheduled_end, external_contact_name, external_contact_phone")
          .eq("project_id", projectId).neq("status", "cancelled"),
      ]);
      for (const r of [acts, subs, lanes, bks]) if (r.error) throw r.error;
      return { acts: acts.data ?? [], subs: subs.data ?? [], lanes: lanes.data ?? [], bks: bks.data ?? [] };
    },
  });

  async function tick(su: any) {
    const { error } = await (supabase as any).from("project_program_subactivities")
      .update({ done: !su.done }).eq("sub_id", su.sub_id);
    if (!error) {
      qc.invalidateQueries({ queryKey: ["field_program_full", projectId] });
      qc.invalidateQueries({ queryKey: ["field_program_today", projectId] });
      setSel((prev: any) => prev); // detail refreshes from query below
    }
  }

  const laneC = (lane: string | null) =>
    data?.lanes.find((l: any) => l.lane_code === lane)?.color || (lane && FP_LANE_C[lane]) || "#8a95a3";

  if (!open) {
    return (
      <Card className="md:col-span-2">
        <button onClick={() => setOpen(true)}
          className="w-full p-4 flex items-center justify-between text-left hover:bg-ink-raised/20">
          <span className="text-[13px] font-medium inline-flex items-center gap-2">
            <Calendar size={14} className="text-accent" /> Full build program
          </span>
          <span className="text-[12px] text-text-muted">view the whole plan →</span>
        </button>
      </Card>
    );
  }
  if (!data) return <Card className="md:col-span-2"><div className="p-5 text-[13px] text-text-muted">Loading program…</div></Card>;

  const dated = data.acts.filter((a: any) => a.start_date);
  if (!dated.length) return null;
  const minS = dated.reduce((m: string, a: any) => (a.start_date < m ? a.start_date : m), dated[0].start_date);
  const maxE = dated.reduce((m: string, a: any) => ((a.end_date ?? a.start_date) > m ? (a.end_date ?? a.start_date) : m), dated[0].end_date ?? dated[0].start_date);
  const start = parseISO(minS); const totalDays = Math.max(Math.round((parseISO(maxE).getTime() - start.getTime()) / 864e5) + 3, 14);
  const DW = 14, LW = 132;
  const idxOf = (d: string) => Math.round((parseISO(d).getTime() - start.getTime()) / 864e5);
  const localKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const todayIdx = idxOf(localKey(new Date()));

  const laneOrder = new Map(data.lanes.map((l: any, i: number) => [l.lane_code, i]));
  const sorted = [...data.acts].sort((x: any, y: any) =>
    ((laneOrder.get(x.lane) ?? 99) as number) - ((laneOrder.get(y.lane) ?? 99) as number) || x.sort_order - y.sort_order);
  const subsBy = new Map<string, any[]>();
  for (const su of data.subs) { if (!subsBy.has(su.activity_id)) subsBy.set(su.activity_id, []); subsBy.get(su.activity_id)!.push(su); }
  const selSubs = sel ? (subsBy.get(sel.activity_id) ?? []) : [];
  const selBk = sel?.booking_id ? data.bks.find((b: any) => b.booking_id === sel.booking_id) : null;

  let lastLane: string | null = "__x";
  return (
    <Card className="md:col-span-2">
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono inline-flex items-center gap-1.5">
            <Calendar size={10} className="text-accent" /> Full build program — view only
          </span>
          <button onClick={() => { setOpen(false); setSel(null); }} className="text-[12px] text-text-muted hover:text-text">close</button>
        </div>
        <div className="overflow-x-auto -mx-1 px-1">
          <div style={{ width: LW + totalDays * DW }} className="relative select-none">
            {sorted.map((a: any) => {
              const rows: any[] = [];
              if (a.lane !== lastLane) {
                lastLane = a.lane;
                rows.push(
                  <div key={`l-${a.lane}`} className="flex items-center gap-1.5 pt-2 pb-0.5">
                    <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: laneC(a.lane) }} />
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">{a.lane}</span>
                  </div>
                );
              }
              const mySubs = subsBy.get(a.activity_id) ?? [];
              const doneN = mySubs.filter((x: any) => x.done).length;
              const external = a.owner !== "internal";
              const sIdx = a.start_date ? idxOf(a.start_date) : null;
              const w = a.start_date ? (idxOf(a.end_date ?? a.start_date) - sIdx! + 1) * DW : 0;
              rows.push(
                <button key={a.activity_id} onClick={() => setSel(a)}
                  className="w-full flex items-center text-left hover:bg-ink-raised/20 rounded"
                  style={{ height: 26 }}>
                  <span className="shrink-0 truncate text-[11px] pr-2" style={{ width: LW, color: external ? "#8a8378" : undefined }}>
                    {a.name}{mySubs.length > 0 && <span className="text-text-dim"> · {doneN}/{mySubs.length}</span>}
                  </span>
                  <span className="relative flex-1" style={{ height: 26 }}>
                    {todayIdx >= 0 && todayIdx < totalDays && (
                      <span className="absolute top-0 bottom-0 pointer-events-none" style={{ left: todayIdx * DW, width: DW, background: "color-mix(in srgb, var(--accent, #e2661a) 10%, transparent)" }} />
                    )}
                    {sIdx !== null && (
                      a.kind === "milestone" || a.kind === "client_deliverable" ? (
                        <span className="absolute" style={{ left: sIdx * DW + DW / 2 - 5, top: 8, width: 10, height: 10, background: laneC(a.lane), transform: "rotate(45deg)", borderRadius: 2 }} />
                      ) : (
                        <span className="absolute rounded" style={{
                          left: sIdx * DW, width: Math.max(w - 2, 6), top: 6, height: 14,
                          background: external || a.kind === "hold" ? `color-mix(in srgb, ${laneC(a.lane)} 18%, transparent)` : laneC(a.lane),
                          border: external || a.kind === "hold" ? `1.5px dashed ${laneC(a.lane)}` : "none",
                        }} />
                      )
                    )}
                  </span>
                </button>
              );
              return rows;
            })}
          </div>
        </div>
        <div className="text-[11px] text-text-dim mt-2">Tap any line for its notes, checklist and booking details.</div>
      </div>

      {sel && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center" onClick={() => setSel(null)}>
          <div className="bg-ink-card w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl border border-line max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-5">
              <div className="flex items-start gap-2.5 mb-1">
                <span className="w-2.5 h-2.5 rounded-sm mt-1.5 shrink-0" style={{ background: laneC(sel.lane) }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold leading-snug">{sel.name}</div>
                  <div className="text-[12px] text-text-muted mt-0.5">
                    {sel.start_date ? format(parseISO(sel.start_date), "EEE d MMM") : "no date"}
                    {sel.end_date && sel.end_date !== sel.start_date ? ` – ${format(parseISO(sel.end_date), "EEE d MMM")}` : ""}
                    {sel.resource ? ` · ${sel.resource}` : ""}
                  </div>
                </div>
                <button onClick={() => setSel(null)} className="text-text-muted hover:text-text text-[18px] leading-none">×</button>
              </div>
              {(sel.plant_needed?.length ?? 0) > 0 && (
                <div className="mt-2 flex gap-1.5 flex-wrap">
                  {sel.plant_needed.map((pl: string) => (
                    <span key={pl} className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-800 border border-amber-500/30">{pl}</span>
                  ))}
                </div>
              )}
              {sel.notes && (
                <div className="mt-3 text-[13px] bg-ink-raised/30 border border-line rounded-lg px-3 py-2">{sel.notes}</div>
              )}
              {selSubs.length > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-1">Checklist</div>
                  {selSubs.map((su: any) => (
                    <div key={su.sub_id} className="flex items-center gap-3 py-1.5">
                      <button onClick={() => void tick(su)}
                        className={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all active:scale-90 ${su.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-line text-transparent"}`}>
                        <CheckCircle2 size={16} strokeWidth={3} className={su.done ? "" : "opacity-0"} />
                      </button>
                      <span className={`text-[13.5px] ${su.done ? "line-through text-text-dim" : ""}`}><CrewTag crew={su.crew} />{su.name}</span>
                    </div>
                  ))}
                </div>
              )}
              {selBk && (
                <div className="mt-3 border border-line rounded-lg px-3 py-2.5">
                  <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-1">Booking</div>
                  <div className="text-[13px] font-medium">{selBk.title} <span className="text-text-muted font-normal">· {selBk.booking_type} · {selBk.status}</span></div>
                  <div className="text-[12px] text-text-muted">
                    {format(new Date(selBk.scheduled_start), "EEE d MMM, HH:mm")}
                    {selBk.scheduled_end ? ` – ${format(new Date(selBk.scheduled_end), "HH:mm")}` : ""}
                  </div>
                  {selBk.external_contact_name && (
                    <div className="text-[13px] mt-1.5">
                      <User size={12} className="inline mr-1 text-text-muted" />
                      {selBk.external_contact_name}
                      {selBk.external_contact_phone && (
                        <a href={`tel:${selBk.external_contact_phone}`} className="ml-2 text-accent font-semibold">
                          {selBk.external_contact_phone}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

// -----------------------------------------------------------------------------
// TodaysWorkCard — the field build plan (mig 345, James's sub-activities
// ticket): today's program activities with their sub-activities as big
// tickable boxes. "Today's Program Activity → Required Sub-Activities → Tick
// Off as Completed." RLS: anyone on the project can read and tick.
// -----------------------------------------------------------------------------
// mig 381 — "my steps": the crew picks Riggers / Tech / Civil once and the
// build plan shows only their steps (steps with no crew tag are everyone's and
// stay). Remembered on the phone, not on the server — a rigger who techs for
// a day just flips it. All hooks sit above the early return below.
type CrewFilter = "all" | "riggers" | "tech" | "civil";
const CREW_FILTER_KEY = "coretel.buildplan.crew";
function readCrewFilter(): CrewFilter {
  try {
    const v = localStorage.getItem(CREW_FILTER_KEY);
    return v === "riggers" || v === "tech" || v === "civil" ? v : "all";
  } catch { return "all"; }
}

function TodaysWorkCard({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const user = useRequireAuth();
  const employeeId = (user as any)?.employee_id ?? null;
  const [crewFilter, setCrewFilterState] = useState<CrewFilter>(readCrewFilter);
  const setCrewFilter = (v: CrewFilter) => {
    setCrewFilterState(v);
    try { localStorage.setItem(CREW_FILTER_KEY, v); } catch { /* private mode — the chip still works for this visit */ }
  };
  const subVisible = (su: any) => crewFilter === "all" || !su.crew || su.crew === crewFilter;
  const { data, isLoading } = useQuery({
    queryKey: ["field_program_today", projectId, employeeId],
    queryFn: async () => {
      const [acts, subs, mine] = await Promise.all([
        (supabase as any).from("project_program_activities")
          .select("activity_id, code, lane, owner, name, resource, kind, start_date, end_date, days, notes, plant_needed, sort_order")
          .eq("project_id", projectId).eq("is_active", true)
          .eq("in_build_plan", true)  // mig 348 — the crew's plan is Coretel's lines
          .not("start_date", "is", null)
          .order("sort_order"),
        (supabase as any).from("project_program_subactivities")
          .select("sub_id, activity_id, name, sort_order, done, done_at, crew")
          .eq("project_id", projectId).order("sort_order"),
        employeeId
          ? (supabase as any).from("personal_bookings")
              .select("booking_id, title, scheduled_start, scheduled_end, location_address, notes, status")
              .eq("assignee_employee_id", employeeId)
              .eq("project_id", projectId)
              .in("status", ["scheduled", "in_progress"])
              .order("scheduled_start")
          : Promise.resolve({ data: [], error: null }),
      ]);
      if (acts.error) throw acts.error;
      if (subs.error) throw subs.error;
      if (mine.error) throw mine.error;
      return { acts: acts.data ?? [], subs: subs.data ?? [], mine: mine.data ?? [] };
    },
  });

  async function tick(su: any) {
    const { error } = await (supabase as any).from("project_program_subactivities")
      .update({ done: !su.done }).eq("sub_id", su.sub_id);
    if (!error) qc.invalidateQueries({ queryKey: ["field_program_today", projectId] });
  }

  if (isLoading || !data) return null;

  const localKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const today = localKey(new Date());
  const weekOut = localKey(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000));
  const subsBy = new Map<string, any[]>();
  const hiddenBy = new Map<string, number>();   // mig 381 — steps the filter hid, per line
  for (const su of data.subs) {
    if (!subVisible(su)) { hiddenBy.set(su.activity_id, (hiddenBy.get(su.activity_id) ?? 0) + 1); continue; }
    if (!subsBy.has(su.activity_id)) subsBy.set(su.activity_id, []);
    subsBy.get(su.activity_id)!.push(su);
  }
  // A line whose steps are ALL another crew's drops out of my plan; a line with
  // no steps at all stays (nothing to filter on).
  const mine = (a: any) => (subsBy.get(a.activity_id)?.length ?? 0) > 0 || !(hiddenBy.get(a.activity_id) ?? 0);
  const onToday = data.acts.filter((a: any) => a.start_date <= today && (a.end_date ?? a.start_date) >= today && mine(a));
  const nextUp = data.acts.filter((a: any) => a.start_date > today && a.start_date <= weekOut && mine(a));
  const myTasks = (data.mine ?? []).filter((t: any) => {
    const dk = localKey(new Date(t.scheduled_start));
    return dk >= today ? dk <= weekOut : true; // upcoming week + anything still open
  });
  // with a crew filter on, an empty plan still shows — the chips have to stay reachable
  if (!onToday.length && !nextUp.length && !myTasks.length && crewFilter === "all") return null;

  const fmtRange = (a: any) => {
    const f = (x: string) => parseISO(x) ? format(parseISO(x), "EEE d MMM") : x;
    return a.end_date && a.end_date !== a.start_date ? `${f(a.start_date)} – ${f(a.end_date)}` : f(a.start_date);
  };

  const LANE_C: Record<string, string> = {
    CIVILS: "#4f97d7", STRUCTURE: "#7a8aa0", ICIO: "#e2661a", POWER: "#37a97f", ACCEPT: "#9b7bd4",
  };
  const laneC = (lane: string | null) => (lane && LANE_C[lane]) || "#8a95a3";

  // sexy tick button — a big round target that fills green (James: "when it
  // comes to the guys seeing it and interacting with it, make it sexy")
  const Tick = ({ su }: { su: any }) => (
    <button
      onClick={() => void tick(su)}
      aria-label={su.done ? "Mark not done" : "Mark done"}
      className={`shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all active:scale-90 ${
        su.done
          ? "bg-emerald-500 border-emerald-500 text-white shadow-sm"
          : "border-line text-transparent hover:border-emerald-400"
      }`}
    >
      <CheckCircle2 size={16} strokeWidth={3} className={su.done ? "" : "opacity-0"} />
    </button>
  );

  const ActivityBlock = ({ a, live }: { a: any; live: boolean }) => {
    const mySubs = subsBy.get(a.activity_id) ?? [];
    const hiddenN = hiddenBy.get(a.activity_id) ?? 0;
    const doneN = mySubs.filter((x: any) => x.done).length;
    const external = a.owner !== "internal";
    const ownerName = external ? String(a.owner).replace(/^(client|subbie|external):/, "") : null;
    const lc = laneC(a.lane);
    const sd = parseISO(a.start_date);
    return (
      <div
        className={`rounded-xl border overflow-hidden ${live ? "border-accent/30 shadow-sm" : "border-line"}`}
        style={{ borderLeft: `4px solid ${lc}` }}
      >
        <div className={`px-3.5 py-3 ${live ? "bg-accent/[0.045]" : ""}`}>
          <div className="flex items-start gap-3">
            {/* date tile */}
            <div className={`shrink-0 w-11 rounded-lg border text-center py-1 leading-tight ${live ? "border-accent/40 bg-accent/10" : "border-line bg-ink-raised/30"}`}>
              <div className={`text-[9px] uppercase font-bold ${live ? "text-accent" : "text-text-muted"}`}>
                {live ? "now" : format(sd, "EEE")}
              </div>
              <div className="text-[16px] font-bold tabular-nums">{format(sd, "d")}</div>
            </div>
            <div className="min-w-0 flex-1">
              <div className={`text-[14px] font-semibold leading-snug ${external ? "text-text-muted" : "text-text"}`}>
                {a.name}
              </div>
              <div className="text-[11.5px] text-text-muted mt-0.5">
                {fmtRange(a)}
                {a.resource ? ` · ${a.resource}` : ""}
                {external && <span className="ml-1.5 text-[10px] px-1.5 py-px rounded-full border border-line">{ownerName} — their work</span>}
              </div>
              {(a.plant_needed?.length ?? 0) > 0 && (
                <div className="mt-1 flex items-center gap-1 flex-wrap">
                  {a.plant_needed.map((pl: string) => (
                    <span key={pl} className="text-[10.5px] font-semibold px-2 py-px rounded-full bg-amber-500/15 text-amber-800 border border-amber-500/30">
                      {pl}
                    </span>
                  ))}
                </div>
              )}
              {a.notes && <div className="text-[11.5px] text-text-muted mt-1">{a.notes}</div>}
              {hiddenN > 0 && (
                <div className="text-[10.5px] text-text-dim mt-1">+ {hiddenN} step{hiddenN === 1 ? "" : "s"} for the other crew — hidden</div>
              )}
            </div>
            {mySubs.length > 0 && (
              <div className="shrink-0 text-right">
                <div className={`text-[15px] font-bold tabular-nums ${doneN === mySubs.length ? "text-emerald-600" : "text-text"}`}>
                  {doneN}<span className="text-text-muted font-normal">/{mySubs.length}</span>
                </div>
                <div className="text-[9px] uppercase text-text-muted">done</div>
              </div>
            )}
          </div>

          {mySubs.length > 0 && (
            <>
              {/* progress bar in the lane colour */}
              <div className="mt-2.5 h-1.5 rounded-full bg-ink-raised/50 overflow-hidden">
                <div className="h-full rounded-full transition-all"
                  style={{ width: `${(doneN / mySubs.length) * 100}%`, background: doneN === mySubs.length ? "#10b981" : lc }} />
              </div>
              <div className="mt-2 space-y-0.5">
                {mySubs.map((su: any) => (
                  <div key={su.sub_id} className="flex items-center gap-3 py-1.5 rounded-lg px-1 -mx-1 active:bg-ink-raised/30">
                    <Tick su={su} />
                    <span className={`text-[13.5px] flex-1 min-w-0 ${su.done ? "line-through text-text-dim" : "text-text"}`}>
                      <CrewTag crew={su.crew} />{su.name}
                    </span>
                    {su.done && su.done_at && (
                      <span className="text-[10px] text-emerald-700 shrink-0 tabular-nums">
                        {format(new Date(su.done_at), "d MMM")}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const allTodaySubs = onToday.flatMap((a: any) => subsBy.get(a.activity_id) ?? []);
  const todayDone = allTodaySubs.filter((x: any) => x.done).length;

  return (
    <Card className="md:col-span-2">
      <div className="p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono inline-flex items-center gap-1.5">
            <CheckCircle2 size={10} className="text-accent" /> Today&apos;s work — build plan
          </div>
          {allTodaySubs.length > 0 && (
            <div className="text-[12px] text-text-muted tabular-nums">
              <span className={todayDone === allTodaySubs.length ? "text-emerald-600 font-semibold" : "text-text font-semibold"}>{todayDone}</span>
              /{allTodaySubs.length} ticked today
            </div>
          )}
        </div>
        {/* mig 381 — my steps. Big targets for gloves. */}
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          <span className="text-[11px] text-text-muted mr-0.5">Show</span>
          {([["all", "Everyone"], ["riggers", "Riggers"], ["tech", "Tech"], ["civil", "Civil"]] as [CrewFilter, string][]).map(([v, label]) => (
            <button key={v} type="button" onClick={() => setCrewFilter(v)}
              className={`px-3 py-1.5 rounded-full text-[12px] font-medium border transition-colors active:scale-95 ${
                crewFilter === v
                  ? v === "tech" ? "bg-sky-600 border-sky-600 text-white" : v === "civil" ? "bg-amber-700 border-amber-700 text-white" : v === "riggers" ? "bg-accent border-accent text-white" : "bg-text text-ink border-text"
                  : "border-line text-text-muted"}`}>
              {label}
            </button>
          ))}
        </div>
        {myTasks.length > 0 && (
          <div className="space-y-2 mb-3">
            {myTasks.map((t: any) => {
              const ts = new Date(t.scheduled_start);
              const isToday = localKey(ts) === today;
              return (
                <div key={t.booking_id}
                  className={`rounded-xl border overflow-hidden ${isToday ? "border-violet-400/50 shadow-sm" : "border-line"}`}
                  style={{ borderLeft: "4px solid #7c3aed" }}>
                  <div className={`px-3.5 py-3 ${isToday ? "bg-violet-500/[0.05]" : ""}`}>
                    <div className="flex items-start gap-3">
                      <div className={`shrink-0 w-11 rounded-lg border text-center py-1 leading-tight ${isToday ? "border-violet-400/50 bg-violet-500/10" : "border-line bg-ink-raised/30"}`}>
                        <div className={`text-[9px] uppercase font-bold ${isToday ? "text-violet-700" : "text-text-muted"}`}>
                          {isToday ? "you" : format(ts, "EEE")}
                        </div>
                        <div className="text-[16px] font-bold tabular-nums">{format(ts, "d")}</div>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-semibold leading-snug">
                          {t.title}
                          <span className="ml-1.5 text-[10px] px-1.5 py-px rounded-full bg-violet-500/10 text-violet-700 border border-violet-400/40">your task</span>
                        </div>
                        <div className="text-[11.5px] text-text-muted mt-0.5">
                          {format(ts, "EEE d MMM, HH:mm")}
                          {t.scheduled_end ? ` – ${format(new Date(t.scheduled_end), "HH:mm")}` : ""}
                          {t.location_address ? ` · ${t.location_address}` : ""}
                        </div>
                        {t.notes && <div className="text-[11.5px] text-text-muted mt-0.5">{t.notes}</div>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {onToday.length === 0 && nextUp.length === 0 && myTasks.length === 0 && crewFilter !== "all" && (
          <div className="text-[12.5px] text-text-muted py-2">Nothing tagged for this crew in the next week — tap <span className="font-medium">Everyone</span> to see the whole plan.</div>
        )}
        {onToday.length > 0 && (
          <div className="space-y-2.5 mb-3">
            {onToday.map((a: any) => <ActivityBlock key={a.activity_id} a={a} live />)}
          </div>
        )}
        {nextUp.length > 0 && (
          <>
            <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-1.5 mt-1">Coming up</div>
            <div className="space-y-2">
              {nextUp.slice(0, 5).map((a: any) => <ActivityBlock key={a.activity_id} a={a} live={false} />)}
              {nextUp.length > 5 && (
                <div className="text-[11px] text-text-dim italic">+ {nextUp.length - 5} more this week</div>
              )}
            </div>
          </>
        )}
        <div className="text-[11px] text-text-dim mt-3">
          Tick it as you do it — your name and time go on the record.
        </div>
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// BuildPlanCard — T-245 (Phase 1 of T-183, backbone for T-202D-driven plans)
//
// Field-friendly view of project_formal_milestones for this project. Key calls:
//
//   • PMO filter: hide is_payment_trigger=true rows. Those are billing/claim
//     milestones (Client PO % triggers etc) — accounting concern, not field.
//   • Group:
//       — Key dates       (is_starred=true) — pinned at top, even if done
//       — In progress     (status='in_progress')
//       — Upcoming        (status='planned' AND planned_date >= today, by date asc)
//       — Recently done   (status='done', last 30d) — collapsed summary
//   • Empty state: "Build plan not set yet" — when manager populates via
//     T-202D's milestone-templates admin UI, this surfaces automatically.
//
// Future hook: when ITPs land (likely as a milestone_code prefix or new flag),
// they'll render with a distinct badge here without further refactor.
// -----------------------------------------------------------------------------
function BuildPlanCard({ projectId }: { projectId: string }) {
  const { data: milestones = [], isLoading } = useQuery({
    queryKey: ["field_project_milestones", projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("project_formal_milestones")
        .select(`
          milestone_id, milestone_code, name, scope_owner,
          planned_date, actual_date, status,
          is_starred, is_payment_trigger, client_visible,
          sort_order, comment
        `)
        .eq("project_id", projectId)
        .eq("is_payment_trigger", false)         // ← PMO/billing milestones hidden
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  if (isLoading) {
    return (
      <Card className="md:col-span-2">
        <div className="p-5 flex items-center gap-2 text-text-muted text-[13px]">
          <Loader2 size={14} className="animate-spin" /> Loading build plan…
        </div>
      </Card>
    );
  }

  if (milestones.length === 0) {
    return (
      <Card className="md:col-span-2">
        <div className="p-5">
          <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-2 inline-flex items-center gap-1.5">
            <Flag size={10} className="text-accent"/> Build plan
          </div>
          <div className="text-[13px] text-text-muted italic">
            Build plan not set yet. Your supervisor will share key dates once the project is in flight.
          </div>
        </div>
      </Card>
    );
  }

  // ---- Group ----
  // LOCAL day keys — toISOString() says "yesterday" before 10am AEST
  const localKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const todayISO = localKey(new Date());
  const thirtyAgoISO = localKey(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));

  const starred = milestones.filter(m => m.is_starred);
  const inProgress = milestones.filter(m => !m.is_starred && m.status === "in_progress");
  const upcoming = milestones
    .filter(m => !m.is_starred && m.status === "planned" && (m.planned_date ?? "") >= todayISO)
    .sort((a, b) => (a.planned_date ?? "9999").localeCompare(b.planned_date ?? "9999"));
  const recentDone = milestones
    .filter(m => !m.is_starred && m.status === "done" && (m.actual_date ?? "") >= thirtyAgoISO)
    .sort((a, b) => (b.actual_date ?? "").localeCompare(a.actual_date ?? ""));

  return (
    <Card className="md:col-span-2">
      <div className="p-5">
        <div className="text-[11px] uppercase tracking-wider2 text-text-muted font-mono mb-3 inline-flex items-center gap-1.5">
          <Flag size={10} className="text-accent"/> Build plan
        </div>

        {starred.length > 0 && (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider2 text-accent font-mono mb-1.5 inline-flex items-center gap-1">
              <Star size={9} /> Key dates
            </div>
            <div className="space-y-1.5">
              {starred.map(m => <MilestoneRow key={m.milestone_id} m={m} highlight />)}
            </div>
          </div>
        )}

        {inProgress.length > 0 && (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-1.5 inline-flex items-center gap-1">
              <Loader2 size={9} className="animate-spin"/> In progress
            </div>
            <div className="space-y-1.5">
              {inProgress.map(m => <MilestoneRow key={m.milestone_id} m={m} />)}
            </div>
          </div>
        )}

        {upcoming.length > 0 && (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-1.5 inline-flex items-center gap-1">
              <Calendar size={9} /> Upcoming
            </div>
            <div className="space-y-1.5">
              {upcoming.slice(0, 6).map(m => <MilestoneRow key={m.milestone_id} m={m} />)}
              {upcoming.length > 6 && (
                <div className="text-[11px] text-text-dim italic pl-5">
                  + {upcoming.length - 6} more upcoming
                </div>
              )}
            </div>
          </div>
        )}

        {recentDone.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wider2 text-text-muted font-mono mb-1.5 inline-flex items-center gap-1">
              <CheckCircle2 size={9} /> Recently done (last 30 days)
            </div>
            <div className="space-y-1.5">
              {recentDone.slice(0, 4).map(m => <MilestoneRow key={m.milestone_id} m={m} muted />)}
              {recentDone.length > 4 && (
                <div className="text-[11px] text-text-dim italic pl-5">
                  + {recentDone.length - 4} more completed earlier
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// mig 382 — what materials have come in for this job, and the button to log
// the next one. Read-only for the crew (a manager ticks the client sheet).
function FieldDeliveriesCard({ projectId }: { projectId: string }) {
  // mig 501 — the job's BOM at a glance (RLS: anyone on the job reads it)
  const { data: pm } = useQuery({
    queryKey: ["project_materials_summary", projectId],
    queryFn: async () => { const { data } = await (supabase as any).from("v_project_materials").select("*").eq("project_id", projectId).maybeSingle(); return data as { lines: number; lines_received: number; lines_partial: number; lines_open: number } | null; },
  });
  const { data: rows = [] } = useQuery({
    queryKey: ["project_deliveries", projectId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("material_deliveries").select(DELIVERY_SELECT)
        .eq("project_id", projectId).order("received_at", { ascending: false }).limit(20);
      if (error) throw error;
      return (data ?? []) as DeliveryRow[];
    },
  });
  return (
    <Card className="md:col-span-2">
      <div className="p-5">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider2 text-text-muted">
            <Truck size={13} /> Materials · {rows.length} deliver{rows.length === 1 ? "y" : "ies"}
          </div>
          <Link to={`/capture/delivery?project=${projectId}`}><Button size="sm" icon={Camera}>Take a delivery</Button></Link>
        </div>
        {pm && pm.lines > 0 && (
          <div className="mb-3 rounded-md border border-line bg-ink-raised/40 px-3 py-2 text-[12.5px] flex items-center gap-3">
            <span className="font-medium">BOM</span>
            <div className="flex-1 h-2 rounded-full bg-ink overflow-hidden"><div className="h-full bg-ok" style={{ width: `${Math.round((pm.lines_received / pm.lines) * 100)}%` }}/></div>
            <span className={`font-mono ${pm.lines_received === pm.lines ? "text-ok" : "text-text-muted"}`}>{pm.lines_received}/{pm.lines} in{pm.lines_partial ? ` · ${pm.lines_partial} part` : ""}{pm.lines_open ? ` · ${pm.lines_open} to come` : ""}</span>
          </div>
        )}
        {rows.length === 0 && <div className="text-[13px] text-text-muted">Nothing logged yet. When materials come in, snap the docket and it lands here.</div>}
        <div className="space-y-2">
          {rows.map(r => <DeliveryCard key={r.delivery_id} row={r} canManage={false} invalidateKeys={[["project_deliveries", projectId]]} />)}
        </div>
      </div>
    </Card>
  );
}

function MilestoneRow({ m, highlight, muted }: { m: any; highlight?: boolean; muted?: boolean }) {
  const date = m.actual_date ?? m.planned_date;
  const dateLabel = date ? format(parseISO(date), "dd MMM yyyy") : "—";
  const StatusIcon = m.status === "done" ? CheckCircle2
                   : m.status === "in_progress" ? Loader2
                   : m.status === "blocked" ? Flag
                   : CircleDashed;
  const statusColour = m.status === "done" ? "text-ok"
                     : m.status === "in_progress" ? "text-accent"
                     : m.status === "blocked" ? "text-danger"
                     : "text-text-muted";
  return (
    <div className={`flex items-start gap-2.5 ${muted ? "opacity-70" : ""}`}>
      <StatusIcon size={12} className={`${statusColour} flex-shrink-0 mt-0.5 ${m.status === "in_progress" ? "animate-spin" : ""}`}/>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          {m.milestone_code && (
            <span className={`text-[10px] font-mono ${highlight ? "text-accent" : "text-text-dim"}`}>{m.milestone_code}</span>
          )}
          <span className={`text-[12px] ${highlight ? "text-text font-medium" : "text-text"}`}>{m.name}</span>
          {m.scope_owner && (
            <span className="text-[10px] text-text-dim">· {m.scope_owner}</span>
          )}
        </div>
        <div className="text-[11px] font-mono text-text-muted mt-0.5">
          {m.actual_date ? "Done " : m.status === "deferred" ? "Deferred " : "Target "}{dateLabel}
        </div>
      </div>
    </div>
  );
}


// mig 381 — who does this step. Small tag before the name so a rigger can see
// at a glance which steps are theirs; "T" is the tech's cabinet work.

function CrewTag({ crew }: { crew?: string | null }) {
  if (!crew) return null;
  const label = crew === "tech" ? "T" : crew === "civil" ? "C" : "R";
  const cls = crew === "tech" ? "bg-sky-600/15 text-sky-700" : crew === "civil" ? "bg-amber-700/15 text-amber-800" : "bg-accent/15 text-accent";
  return <span className={`inline-block align-middle mr-1.5 text-[10px] font-semibold px-1 rounded ${cls}`} title={crew === "tech" ? "Technician" : crew === "civil" ? "Civil" : "Riggers"}>{label}</span>;
}
